/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Groups ViewAppointments hook output for presentational subcomponents.
 * @module pages/viewAppointments/buildViewAppointmentsViewModel
 */

import { companyNameCollator } from "./useViewAppointmentsContext.js";
import { filterDropdownSelectStyles } from "./viewAppointmentsSelectStyles.js";

/**
 * Builds grouped props for `ViewAppointmentsView` and its child panels.
 * @param {Object} ctx - Context from `useViewAppointmentsContext`
 * @param {Object} fetching - Fetch helpers
 * @param {Object} selectStyles - Memoized react-select style objects
 * @param {Object} handlers - Merged handler maps from sub-hooks
 * @returns {Object}
 */
export const buildViewAppointmentsViewModel = (
  ctx,
  fetching,
  selectStyles,
  handlers,
) => ({
  header: {
    t: ctx.t,
    navigate: ctx.navigate,
    eventData: ctx.eventData,
    onNavigateBack: handlers.handleNavigateBack,
  },
  search: {
    t: ctx.t,
    companyNameCollator,
    searchQuery: ctx.searchQuery,
    userData: ctx.userData,
    buyerCompanies: ctx.buyerCompanies,
    selectedBuyerCompany: ctx.selectedBuyerCompany,
    buyerExecutives: ctx.buyerExecutives,
    selectedBuyerExecutive: ctx.selectedBuyerExecutive,
    requirements: ctx.requirements,
    selectedRequirement: ctx.selectedRequirement,
    primarySelectStyles: selectStyles.primarySelectStyles,
    executiveSelectStyles: selectStyles.executiveSelectStyles,
    requirementSelectStyles: selectStyles.requirementSelectStyles,
    onSearch: handlers.handleSearch,
    onBuyerCompanyChange: handlers.handleBuyerCompanyChange,
    onBuyerExecutiveChange: handlers.handleBuyerExecutiveChange,
    onRequirementChange: handlers.handleRequirementChange,
    onToggleFiltersDropdown: handlers.toggleFiltersDropdown,
    onPrintCalendar: handlers.handlePrintCalendar,
  },
  table: {
    t: ctx.t,
    userData: ctx.userData,
    filteredRequests: ctx.filteredRequests,
    selectedBuyerCompany: ctx.selectedBuyerCompany,
    selectedBuyerExecutive: ctx.selectedBuyerExecutive,
    selectedRequirement: ctx.selectedRequirement,
    debouncedSearch: ctx.debouncedSearch,
    totalAppointments: ctx.totalAppointments,
    currentPage: ctx.currentPage,
    resultsLimit: ctx.resultsLimit,
    getAppointmentStatusStyleKey: handlers.getAppointmentStatusStyleKey,
    onActionsDropdownClick: handlers.handleActionsDropdownClick,
    onResultsLimitChange: handlers.handleAppointmentsResultsLimitChange,
    onPageChange: handlers.handleAppointmentsPageChange,
  },
  calendar: {
    t: ctx.t,
    getCalendarAppointments: fetching.getCalendarAppointments,
    allAppointmentsForConflict: ctx.allAppointmentsForConflict,
    onDateSelect: handlers.handleDateSelect,
    onAppointmentReschedule: handlers.handleAppointmentReschedule,
    eventData: ctx.eventData,
    participatingCompanyData: ctx.participatingCompanyData,
    onConfirm: handlers.handleConfirm,
    isConfirmButtonDisabled: fetching.isConfirmButtonDisabled(),
    senderAppointments: ctx.senderAppointments,
    highlightedRequirementId: ctx.selectedRequirement?.value,
  },
  overlays: {
    t: ctx.t,
    isLoading: ctx.isLoading,
    activeDropdownId: ctx.activeDropdownId,
    dropdownDirection: ctx.dropdownDirection,
    dropdownPosition: ctx.dropdownPosition,
    isFiltersDropdownOpen: ctx.isFiltersDropdownOpen,
    filtersDropdownDirection: ctx.filtersDropdownDirection,
    filtersDropdownPosition: ctx.filtersDropdownPosition,
    selectedStatusFilter: ctx.selectedStatusFilter,
    selectedRequirementsFilter: ctx.selectedRequirementsFilter,
    filterDropdownSelectStyles,
    showSuccessMessage: ctx.showSuccessMessage,
    isSlidingUp: ctx.isSlidingUp,
    showRescheduleSuccessMessage: ctx.showRescheduleSuccessMessage,
    isRescheduleSlidingUp: ctx.isRescheduleSlidingUp,
    showConfirmModal: ctx.showConfirmModal,
    showVirtualMeetingModal: ctx.showVirtualMeetingModal,
    showVirtualAppointmentErrorModal: ctx.showVirtualAppointmentErrorModal,
    selectedAppointmentForVirtual: ctx.selectedAppointmentForVirtual,
    isConvertingToVirtual: ctx.isConvertingToVirtual,
    virtualAppointmentError: ctx.virtualAppointmentError,
    eventData: ctx.eventData,
    confirmationDialogRef: ctx.confirmationDialogRef,
    virtualAppointmentDialogRef: ctx.virtualAppointmentDialogRef,
    virtualAppointmentErrorDialogRef: ctx.virtualAppointmentErrorDialogRef,
    onActiveDropdownSeeProfile: handlers.handleActiveDropdownSeeProfile,
    onActiveDropdownSeeRequest: handlers.handleActiveDropdownSeeRequest,
    onActiveDropdownMakeVirtual: handlers.handleActiveDropdownMakeVirtual,
    isActiveDropdownMakeVirtualDisabled:
      handlers.isActiveDropdownMakeVirtualDisabled,
    onClearFilters: handlers.handleClearFilters,
    onStatusFilterChange: ctx.setSelectedStatusFilter,
    onRequirementsFilterChange: ctx.setSelectedRequirementsFilter,
    onConfirmModalClose: handlers.handleConfirmModalCancel,
    onConfirmModalConfirm: handlers.handleConfirmModalConfirm,
    onVirtualAppointmentClose: handlers.handleVirtualAppointmentCancel,
    onVirtualAppointmentConfirm: handlers.handleVirtualAppointmentConfirm,
    onVirtualAppointmentErrorClose: handlers.handleVirtualAppointmentErrorClose,
  },
  registeredMembersTitle: ctx.t("pages.viewAppointments.registeredMembers"),
});
